import { FC, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Button } from 'components';
import { ViewStateType } from 'components/ProductTemplates/types';
import { WineCardListSkeleton } from '../../../components/Skeleton';
import { AppContext } from '../../../context/ContextProvider';
import { getRegionColors, handleImageError } from '../../../helpers';
import { capitalizeFirstLetter, classNames } from '../../../utils';
import useFadeInOnScroll from '../../hooks/useFadeInOnScroll';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import { InvestOffer } from '../types';
import noResultImage from '../../../assets/images/no_results_image.png';
import { isAllowedCampaignPurchase } from '../helpers';

export interface InvestOfferProps {
  offers: InvestOffer[];
  onItemSelect: (offer: InvestOffer, viewIn?: ViewStateType, sourceRow?: string) => void;
  loading?: boolean;
}
const lowStockQty = 5;
const InvestPromotion: FC<InvestOfferProps> = ({ offers, onItemSelect, loading = false }) => {
  const {
    state: {
      settings: { accountInfo },
    },
  } = useContext(AppContext);

  const { t } = useTranslation();
  const { formatter: currencyFormatter } = useContext(AppContext);
  const { isLoading, results, lastItemRef } = useInfiniteScroll(offers);
  const processIntersectionObserverEntry = useCallback((entry: IntersectionObserverEntry) => {
    entry.target.classList.toggle('flex', entry.isIntersecting);
  }, []);

  const { isItemVisible, fadeOnScrollClassName } = useFadeInOnScroll({ isLoading, processIntersectionObserverEntry });

  return (
    <div className="flex flex-wrap justify-center w-full gap-5 overflow-x-hidden bg-gray-50 dark:bg-slate-900 p-5 sm:p-7 overflow-y-auto max-h-[calc(100dvh-180px)]">
      {loading && (
        <div className="w-full">
          <WineCardListSkeleton count={6} />
        </div>
      )}
      {!loading && results.length === 0 && (
        <div className="animate-[fade-in_5s_ease-out] w-full h-full flex items-center justify-center">
          <div className="w-full max-w-md mx-auto bg-white dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl shadow-lg dark:shadow-slate-950/50 border border-gray-200 dark:border-slate-600 p-10 text-center">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-gray-100 to-gray-50 dark:from-slate-700 dark:to-slate-700/50 flex items-center justify-center">
              <img className="w-16 h-16 object-contain" src={noResultImage} alt={t('common:noResult.title')} />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{t('common:noResult.title')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
              {t('common:noResult.subTitle', 'Check back later for new investment opportunities')}
            </p>
          </div>
        </div>
      )}
      {!loading &&
        results.length > 0 &&
        results.map((item, index) => {
          const {
            id,
            name: title,
            subtitle: subTitle,
            price,
            region,
            unitSize: unit,
            mainImage,
            expiryDate,
            initialQuantity,
            quantityOutstanding,
            offerSource,
            lwin18,
            numberOfUnitsAllowed,
            campaignId,
          } = item;

          const { isAllowed } = isAllowedCampaignPurchase({
            campaignId,
            lwin18: lwin18,
            quantity: quantityOutstanding,
            numberOfUnitsAllowed,
            id: accountInfo?.portfolios?.map((x) => x.portfolioName).toString(),
          });

          let lastElemRefOption = { ref: isItemVisible };
          if (results.length === index + 1) {
            lastElemRefOption = { ref: lastItemRef };
          }

          const expDate = `${moment(expiryDate).format('YYYY-MM-DD')}`;
          const { color: regionColor, textColor: regionTextColor } = getRegionColors(region);
          const isLowStock = quantityOutstanding <= lowStockQty && offerSource !== 'prismic';

          return (
            <div
              {...lastElemRefOption}
              key={`${id}`}
              id={`${id}`}
              onClick={() =>
                offerSource === 'prismic' ? onItemSelect(item) : onItemSelect(item, ViewStateType.INVEST_MORE)
              }
              className={classNames(
                'flex w-full sm:w-[340px] rounded-2xl overflow-hidden cursor-pointer',
                'bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700',
                'shadow-sm hover:shadow-xl hover:-translate-y-0.5 hover:border-gray-200 dark:hover:border-slate-600',
                'transition-all duration-200 ease-out',
                fadeOnScrollClassName,
              )}
            >
              {/* Region accent bar */}
              <div className="w-1.5 flex-shrink-0" style={{ backgroundColor: regionColor || '#757587' }} />

              {/* Main content */}
              <div className="flex flex-col flex-1 p-4">
                {/* Wine image */}
                <div className="bg-gradient-to-b from-gray-50 to-gray-100 dark:from-slate-700 dark:to-slate-600 rounded-xl flex items-center justify-center mb-3 py-4">
                  <img
                    className="h-[130px] object-contain drop-shadow-sm"
                    alt={title}
                    src={mainImage}
                    onError={handleImageError}
                  />
                </div>

                {/* Wine name + subtitle */}
                <div className="mb-3">
                  <h3 className="text-14 font-semibold text-gray-900 dark:text-white leading-snug line-clamp-2 mb-1">
                    {title}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{subTitle}</p>
                </div>

                {/* Price card */}
                <div className="bg-gray-50 dark:bg-slate-700/60 rounded-lg p-3 mb-3">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
                      {t('invest:price', 'Price')}
                    </span>
                    <span className="text-base font-bold text-gray-900 dark:text-white">
                      {currencyFormatter.format(Number(price), true)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
                      {t('invest:unitSize', 'Unit size')}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-300">{unit}</span>
                  </div>
                </div>

                {/* Stock / expiry info */}
                {offerSource !== 'prismic' && (
                  <div className="flex items-center gap-2 mb-3">
                    <div
                      className={classNames(
                        'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border',
                        isLowStock
                          ? 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 font-medium'
                          : 'border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50 text-gray-600 dark:text-gray-300',
                      )}
                    >
                      {isLowStock && (
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                        </span>
                      )}
                      {isLowStock ? t('invest:lowStock') : t('invest:campaign_stock_date')}
                    </div>
                  </div>
                )}
                {offerSource === 'prismic' && expiryDate && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-3">
                    <svg
                      className="w-3.5 h-3.5 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span>{expDate}</span>
                  </div>
                )}

                {/* Bottom action row */}
                <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100 dark:border-slate-700">
                  {offerSource === 'prismic' ? (
                    <span
                      style={{ backgroundColor: regionColor || '#757587' }}
                      className={classNames('px-2.5 py-1 rounded-md text-xs font-semibold', regionTextColor)}
                    >
                      {region ? capitalizeFirstLetter(region) : ''}
                    </span>
                  ) : (
                    <div
                      className={classNames(
                        'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border',
                        isLowStock
                          ? 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 font-medium'
                          : 'border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50 text-gray-600 dark:text-gray-300',
                      )}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {quantityOutstanding} / {initialQuantity} {t('invest:available', 'available')}
                    </div>
                  )}

                  <Button
                    isDisable={!isAllowed}
                    className="text-sm font-medium rounded-full px-4 !h-[34px] flex justify-center bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 items-center text-white shadow-md shadow-orange/25 dark:shadow-teal-500/25 hover:shadow-lg disabled:opacity-50 disabled:shadow-none transition-all"
                    onClick={() => {
                      if (offerSource === 'prismic') {
                        onItemSelect(item);
                      } else {
                        onItemSelect(item, ViewStateType.INVEST_MORE);
                      }
                    }}
                    props={{
                      name: 'requestTab',
                    }}
                  >
                    {t('invest:investNow', 'Invest now')}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      <div className="w-full h-[100px]" />
    </div>
  );
};

export default InvestPromotion;
